/**
 * Zahlungsstaffel — aus Angebotssumme und Stufen werden Betraege in Cent (Befund B-1, GATE-Z1).
 *
 * WARUM es diese Datei gibt: `POST /events/:id/payment-plan` kennt genau zwei Stufen (Anzahlung +
 * Restzahlung). Eine Staffel ueber drei oder vier Raten — bei groesseren Caterings mit Vorlaufkosten
 * ueblich — liess sich bisher nur als Einzelposten von Hand zusammenklicken, ohne jeden Bezug zur
 * Angebotssumme (`docs/KONZEPT-ZAHLUNGSPLAN.md` § 3, B-1).
 *
 * WARUM sie rein ist: Betraege sind server-autoritativ. Weder das Modell noch die Weboberflaeche
 * rechnet eine Stufe aus — die Oberflaeche darf eine Vorschau ZEIGEN, verbindlich ist allein diese
 * Funktion. Sie kennt keine Datenbank, keine Faelligkeiten und keine Zahlungstypen; das ist Sache
 * der Route (Z1.2).
 *
 * WARUM SIE HIER LIEGT und nicht in `apps/api/src/lib` (dort entstand sie in Z1.1, umgezogen mit
 * Z1.3): Ueber dieselbe Frage urteilen jetzt ZWEI Stellen — die Route beim Anlegen und die
 * Eingabemaske, die dem Betreiber waehrend des Tippens zeigt, was seine Stufen ergeben und ob sie
 * gueltig sind (`apps/web/src/pages/event-detail.tsx`). `apps/web` kann nicht aus `apps/api`
 * importieren; eine zweite Kopie wuerde auseinanderlaufen, und die Oberflaeche wuerde einen Plan
 * durchlassen, den die Route abweist — oder schlimmer: eine Vorschau zeigen, die von den spaeter
 * angelegten Zeilen abweicht. Dieselbe Begruendung wie bei `ungedeckteNullpreisPositionen` und
 * `paketPreisart` in `index.ts`, dieselbe Ratsche-Idee: `apps/api/test/staffel-eine-rechenstelle.test.ts`.
 *
 * DIE EINE REGEL, an der alles haengt: Die Summe der Stufen ergibt die Angebotssumme — immer.
 * Deshalb ist die LETZTE Stufe die ausgleichende: sie bekommt, was nach den vorherigen uebrig
 * bleibt. Ohne diese Festlegung waere ein Plan aus 3 x 33,33 % auf 1.000,01 EUR um 11 Cent
 * unterdeckt, und die P0-Anzeige wuerde bei jeder krummen Summe Alarm schlagen.
 *
 * DAMIT DAS KEIN FREIBRIEF WIRD: Eine Luecke ist kein Rest. Wer „50 % + 20 %" angibt, meint nicht
 * „50 % + 50 %" — die letzte Stufe stillschweigend auf das Zweieinhalbfache zu setzen waere eine
 * Falschaussage ueber das, was der Betreiber eingegeben hat. Der Ausgleich ist deshalb auf
 * RESTTOLERANZ_BP der Angebotssumme gedeckelt (siehe dort), alles darueber wird abgewiesen.
 */

/** Eine Stufe traegt GENAU EINES: einen Prozentsatz (Basispunkte) oder einen festen Betrag. */
export interface Staffelstufe {
  /** Anteil an der Angebotssumme in Basispunkten (10000 = 100 %). */
  prozentBp?: number | null;
  /** Fester Betrag in Cent. */
  betragCents?: number | null;
}

export type StaffelFehler =
  | "keine_stufen"
  | "keine_angebotssumme"
  | "stufe_unklar"
  | "prozent_ungueltig"
  | "betrag_ungueltig"
  | "prozent_zu_hoch"
  | "betrag_zu_gross"
  | "stufe_leer"
  | "rest_zu_gross";

export type Staffelergebnis =
  | {
      ok: true;
      /** Ein Betrag je Stufe, in der Reihenfolge der Eingabe. Summe = Angebotssumme. */
      betraegeCents: number[];
      /**
       * Um wie viele Cent die letzte Stufe von ihrer Angabe abweicht (Rundungsausgleich).
       * Wird ausgewiesen statt still zu passieren — die Route kann es protokollieren.
       */
      restausgleichCents: number;
    }
  | { ok: false; fehler: StaffelFehler; meldung: string; stufe?: number };

/**
 * Wie weit die letzte Stufe hoechstens von ihrer Angabe abweichen darf: 1 % der Angebotssumme,
 * mindestens aber ein Cent je Stufe (bei Kleinbetraegen ist 1 % weniger als der Rundungsrest).
 * Rundung kann nie mehr als einen halben Cent je Stufe verschieben — der Deckel ist also
 * grosszuegig genug fuer jede Rundung und eng genug, um eine echte Luecke aufzudecken.
 */
const RESTTOLERANZ_BP = 100;

import { euro } from "./format";

function fehler(fehler: StaffelFehler, meldung: string, stufe?: number): Staffelergebnis {
  return stufe === undefined ? { ok: false, fehler, meldung } : { ok: false, fehler, meldung, stufe };
}

/**
 * Was eine EINZELNE Stufe nominal ergibt — ohne Pruefung, ohne Restausgleich. `null`, wenn die
 * Stufe keine brauchbare Angabe traegt.
 *
 * WOFUER: Die Eingabemaske zeigt waehrend des Tippens, was die Stufen ergeben — auch dann, wenn
 * die Staffel als Ganzes (noch) ungueltig ist und `berechneStaffel` deshalb gar keine Betraege
 * liefert. Ohne diese Funktion muesste die Oberflaeche `angebotssumme * prozent / 10000` selbst
 * hinschreiben; das waere die zweite Rechenstelle, die es hier nie geben soll. Verbindlich bleibt
 * `berechneStaffel` — dies ist die Vorschau eines einzelnen Feldes, kein Urteil ueber den Plan.
 */
export function stufeSollCents(angebotssummeCents: number, stufe: Staffelstufe): number | null {
  if (!Number.isInteger(angebotssummeCents) || angebotssummeCents <= 0) return null;
  const hatProzent = stufe.prozentBp !== null && stufe.prozentBp !== undefined;
  const hatBetrag = stufe.betragCents !== null && stufe.betragCents !== undefined;
  if (hatProzent === hatBetrag) return null;
  if (hatProzent) {
    const bp = stufe.prozentBp as number;
    if (!Number.isInteger(bp) || bp <= 0 || bp > 10000) return null;
    return Math.round((angebotssummeCents * bp) / 10000);
  }
  const cents = stufe.betragCents as number;
  if (!Number.isInteger(cents) || cents <= 0) return null;
  return cents;
}

export function berechneStaffel(angebotssummeCents: number, stufen: readonly Staffelstufe[]): Staffelergebnis {
  if (!Array.isArray(stufen) || stufen.length === 0) {
    return fehler("keine_stufen", "Eine Staffel braucht mindestens eine Stufe.");
  }
  // 0 EUR ist keine Angebotssumme, sondern der Zustand „noch kein Angebot gerechnet" — dieselbe
  // Lesart wie in `zahlungsdeckung.ts`. Bruchteil-Cent gibt es nicht: die Spalte ist integer.
  if (
    typeof angebotssummeCents !== "number" ||
    !Number.isFinite(angebotssummeCents) ||
    !Number.isInteger(angebotssummeCents) ||
    angebotssummeCents <= 0
  ) {
    return fehler("keine_angebotssumme", "Ohne Angebotssumme laesst sich keine Staffel rechnen — zuerst eine Variante waehlen.");
  }

  const soll: number[] = [];
  let summeBp = 0;
  let summeBetragCents = 0;

  for (let i = 0; i < stufen.length; i += 1) {
    const stufe = stufen[i] ?? {};
    const hatProzent = stufe.prozentBp !== null && stufe.prozentBp !== undefined;
    const hatBetrag = stufe.betragCents !== null && stufe.betragCents !== undefined;
    // Beides gesetzt waere zweideutig, keines von beidem leer — in beiden Faellen muesste die
    // Funktion raten, und geratene Betraege sind genau das, was hier nie passieren darf.
    if (hatProzent === hatBetrag) {
      return fehler("stufe_unklar", `Stufe ${i + 1} braucht genau eines: einen Prozentsatz ODER einen Betrag.`, i);
    }

    if (hatProzent) {
      const bp = stufe.prozentBp as number;
      if (!Number.isInteger(bp) || bp <= 0 || bp > 10000) {
        return fehler("prozent_ungueltig", `Stufe ${i + 1}: Der Prozentsatz muss ueber 0 und hoechstens 100 sein.`, i);
      }
      summeBp += bp;
      // Die Multiplikation steht genau einmal im Haus — in `stufeSollCents`.
      soll.push(stufeSollCents(angebotssummeCents, stufe) as number);
    } else {
      const cents = stufe.betragCents as number;
      if (!Number.isInteger(cents) || cents <= 0) {
        return fehler("betrag_ungueltig", `Stufe ${i + 1}: Der Betrag muss ueber 0,00 EUR liegen und ganze Cent tragen.`, i);
      }
      summeBetragCents += cents;
      soll.push(cents);
    }
  }

  if (summeBp > 10000) {
    return fehler("prozent_zu_hoch", `Die Stufen ergeben ${(summeBp / 100).toFixed(2)} % — mehr als die Angebotssumme geht nicht.`);
  }
  if (summeBetragCents > angebotssummeCents) {
    return fehler("betrag_zu_gross", `Die festen Betraege ergeben ${euro(summeBetragCents)} und ueberschreiten die Angebotssumme von ${euro(angebotssummeCents)}.`);
  }

  const letzte = soll.length - 1;
  const sollLetzteCents = soll[letzte] as number;
  let vorherigeCents = 0;
  for (let i = 0; i < letzte; i += 1) vorherigeCents += soll[i] as number;
  const restCents = angebotssummeCents - vorherigeCents;

  if (restCents <= 0) {
    return fehler("stufe_leer", `Fuer die letzte Stufe bleibt nichts uebrig — die vorherigen Stufen schoepfen die Angebotssumme von ${euro(angebotssummeCents)} bereits aus.`, letzte);
  }

  const abweichungCents = Math.abs(restCents - sollLetzteCents);
  const toleranzCents = Math.max(soll.length, Math.round((angebotssummeCents * RESTTOLERANZ_BP) / 10000));
  if (abweichungCents > toleranzCents) {
    return fehler(
      "rest_zu_gross",
      `Die Stufen decken die Angebotssumme nicht: die letzte Stufe muesste ${euro(restCents)} tragen statt der angegebenen ${euro(sollLetzteCents)}. Bitte die Anteile so waehlen, dass sie zusammen die Angebotssumme ergeben.`,
      letzte,
    );
  }

  const betraegeCents = soll.slice(0, letzte);
  betraegeCents.push(restCents);
  return { ok: true, betraegeCents, restausgleichCents: restCents - sollLetzteCents };
}

/**
 * Die erste Stufe, die FRUEHER faellig wird als eine ihrer Vorgaengerinnen — oder `null`, wenn die
 * Faelligkeiten in der Reihenfolge stehen, in der die Stufen stehen.
 *
 * WARUM DAS GEPRUEFT WIRD, obwohl die Betraege davon unberuehrt bleiben: Eine Staffel ist eine
 * Abfolge, keine Menge. Laeuft Stufe 2 vor Stufe 1, stimmt die Summe weiterhin — aber der Kunde
 * bekommt die Restzahlung vor der Anzahlung, das Mahnwesen mahnt in der falschen Reihenfolge, und
 * der Zahlungsplan im Angebot behauptet eine Vereinbarung, die die Zeilen nicht abbilden. Das
 * faellt niemandem beim Anlegen auf, sondern Wochen spaeter beim Kunden.
 *
 * BEWUSST ERLAUBT: gleiche Daten (zwei Stufen am selben Tag sind ungewoehnlich, aber nicht falsch)
 * und Luecken ohne Datum (die weist die Route eigenstaendig ab, `stufe_ohne_faelligkeit`) — hier
 * wird eine Stufe ohne Datum uebersprungen und mit der letzten bekannten Faelligkeit verglichen.
 *
 * Die Daten sind ISO-Tagesdaten (YYYY-MM-DD); in dieser Form ist der Zeichenkettenvergleich
 * identisch mit dem Datumsvergleich — deshalb kein `Date`, keine Zeitzone, keine Sommerzeit.
 */
export function ersteRueckwaertsStufe(faelligkeiten: readonly (string | null | undefined)[]): number | null {
  let bisher: string | null = null;
  for (let i = 0; i < faelligkeiten.length; i += 1) {
    const datum = faelligkeiten[i];
    if (!datum) continue;
    if (bisher !== null && datum < bisher) return i;
    bisher = datum;
  }
  return null;
}

/* ==========================================================================================
 * Z2.1 — Die Neuverteilung: ein geaendertes Angebot auf die OFFENEN Stufen umlegen (Befund B-2)
 * ========================================================================================== */

/**
 * Die Felder einer Zahlungszeile, die fuer die Neuverteilung zaehlen (mehr wird nicht gelesen) —
 * dieselbe schmale Form wie `Deckungszeile` in `apps/api/src/lib/zahlungsdeckung.ts`.
 */
export interface Neuverteilungszeile {
  amountCents: number | null | undefined;
  status: string | null | undefined;
}

export type NeuverteilungFehler =
  | "keine_angebotssumme"
  | "keine_stufen"
  | "keine_offene_stufe"
  | "nichts_zu_verteilen";

export interface Neuverteilungsaenderung {
  /** Index in der uebergebenen Liste — die Route bildet ihn auf die Zeilen-ID ab. */
  index: number;
  altCents: number;
  neuCents: number;
}

export type Neuverteilungsergebnis =
  | {
      ok: true;
      /**
       * Ein Eintrag je OFFENER Stufe, auch wenn sich ihr Betrag nicht aendert (dann ist
       * `neuCents === altCents`). Bezahlte, erstattete, fehlgeschlagene und stornierte Stufen
       * fehlen hier bewusst — sie sind keine Kandidaten, nicht einmal unveraenderte.
       */
      aenderungen: Neuverteilungsaenderung[];
      /** Summe der Zeilen, die zum Plan zaehlen, aber nicht offen sind (bezahlt/erstattet/gescheitert). */
      gebundeneSummeCents: number;
      offeneSummeAltCents: number;
      /** Was die offenen Stufen zusammen tragen muessen: Angebotssumme minus gebundene Summe. */
      offeneSummeNeuCents: number;
    }
  | { ok: false; fehler: NeuverteilungFehler; meldung: string };

/**
 * Offen heisst: die Forderung steht noch aus und ist noch nicht abgeschlossen.
 *
 * Q11 (BUGHUNT-2026-08-15): DIE eine Definition des Paares ('pending','processing') — vorher
 * stand das Literal an sechs weiteren Stellen in `apps/api` (Routen, Kundenhistorie). Wer hier
 * einen Status ergaenzt, zieht die SQL-Paritaets-Kommentare nach (siehe
 * `apps/api/src/lib/kundenhistorie.ts` — dort steht das Paar als rohes SQL-Literal).
 */
export const OFFENE_ZAHLUNGSSTATUS = ["pending", "processing"] as const;
const OFFENE_STATUS = new Set<string>(OFFENE_ZAHLUNGSSTATUS);

/** Zaehlt `status` zu den offenen Zahlungsstatus (pending/processing)? */
export function istOffenerZahlungsstatus(status: string | null | undefined): boolean {
  return status != null && OFFENE_STATUS.has(status);
}

/**
 * Verteilt die neue Angebotssumme auf die offenen Stufen eines bestehenden Zahlungsplans.
 *
 * WARUM ES DIESE FUNKTION GIBT (Befund B-2, `docs/KONZEPT-ZAHLUNGSPLAN.md` § 3): Die
 * Angebotssumme wird an genau einer Stelle gelesen — beim Anlegen des Plans. Danach besteht
 * keine Verbindung mehr. Wird das Angebot spaeter geaendert (andere Variante gewaehlt,
 * Positionen ergaenzt), bleiben die Zahlungsposten auf dem alten Betrag stehen. P0 macht diese
 * Luecke sichtbar; hier entsteht die Rechnung, die sie schliesst.
 *
 * WAS SIE NICHT TUT: sie schreibt nichts, sie kennt keine Datenbank und sie loest nichts aus.
 * Sie beantwortet genau eine Frage — welchen Betrag traegt jede offene Stufe hinterher? Die
 * Route (Z2.2) entscheidet, ob sie das anwendet, entwertet dabei die Zahlungslinks und
 * protokolliert. Und sie laeuft NIE automatisch: die Anpassung braucht immer den Klick
 * (GATE-Z2, Antoine 11.08.2026).
 *
 * DIE VERTEILUNG IST ANTEILIG, nicht gleichmaessig: wer 30/40/30 vereinbart hat, hat ein
 * Verhaeltnis vereinbart, keine drei Zahlen. Eine Erhoehung um 500 EUR gleichmaessig auf die
 * offenen Stufen zu legen wuerde das vereinbarte Verhaeltnis kippen, ohne dass es jemand merkt.
 *
 * WARUM DER REST HIER ANDERS LAEUFT ALS IN `berechneStaffel`: Dort faengt die LETZTE Stufe den
 * Rundungsrest, weil es eine ausgezeichnete letzte Stufe gibt (die Restzahlung). Hier gibt es
 * die nicht zwingend — die letzte Stufe kann bezahlt und damit gar kein Kandidat sein. Deshalb
 * das Verfahren des groessten Restes (wie bei der Rabattverteilung in `index.ts`): jede Stufe
 * bekommt ihren abgerundeten Anteil, die uebrigen Cent gehen an die Stufen mit dem groessten
 * abgeschnittenen Bruchteil, bei Gleichstand an die vordere. Deterministisch, cent-genau.
 */
export function verteileNeu(angebotssummeCents: number, zeilen: readonly Neuverteilungszeile[]): Neuverteilungsergebnis {
  if (
    typeof angebotssummeCents !== "number" ||
    !Number.isFinite(angebotssummeCents) ||
    !Number.isInteger(angebotssummeCents) ||
    angebotssummeCents <= 0
  ) {
    return { ok: false, fehler: "keine_angebotssumme", meldung: "Ohne Angebotssumme laesst sich der Zahlungsplan nicht anpassen — zuerst eine Variante waehlen." };
  }
  if (!Array.isArray(zeilen) || zeilen.length === 0) {
    return { ok: false, fehler: "keine_stufen", meldung: "Es gibt keinen Zahlungsplan, der angepasst werden koennte." };
  }

  const offeneIndizes: number[] = [];
  const offeneAltCents: number[] = [];
  let gebundeneSummeCents = 0;
  let zaehltZumPlan = 0;

  for (let i = 0; i < zeilen.length; i += 1) {
    const zeile = zeilen[i] ?? {};
    // Storniert ist eine zurueckgezogene Forderung und gehoert nicht mehr zum Plan — dieselbe
    // Lesart wie `berechneDeckung`. Wer sie mitzaehlte, verteilte auf die uebrigen Stufen zu
    // wenig und liesse den Plan dauerhaft unterdeckt.
    if (zeile.status === "canceled") continue;
    zaehltZumPlan += 1;
    // Ein nicht-endlicher Wert ist kein Betrag; er zaehlt als 0 statt die Summe zu NaN zu machen.
    const betrag = typeof zeile.amountCents === "number" && Number.isFinite(zeile.amountCents) ? Math.round(zeile.amountCents) : 0;
    if (typeof zeile.status === "string" && OFFENE_STATUS.has(zeile.status)) {
      offeneIndizes.push(i);
      // Ein negativer Altbetrag waere ein kaputtes Gewicht; als 0 verliert er nur sein Gewicht,
      // die Stufe bleibt Kandidatin und bekommt ihren Mindestanteil.
      offeneAltCents.push(Math.max(betrag, 0));
      continue;
    }
    // Bezahlt, erstattet, fehlgeschlagen: die Forderung besteht (oder bestand) und ist
    // gebunden. Erstattet und fehlgeschlagen zaehlen mit, weil der Anspruch weiterbesteht —
    // ein fehlgeschlagener Einzug wird wiederholt, eine Erstattung macht die Forderung wieder
    // offen. Wer sie herausrechnete, verteilte den bereits gedeckten Teil ein zweites Mal.
    gebundeneSummeCents += betrag;
  }

  if (zaehltZumPlan === 0) {
    return { ok: false, fehler: "keine_stufen", meldung: "Es gibt keinen Zahlungsplan, der angepasst werden koennte — alle Stufen sind storniert." };
  }
  if (offeneIndizes.length === 0) {
    return {
      ok: false,
      fehler: "keine_offene_stufe",
      meldung: `Der Zahlungsplan hat keine offene Stufe mehr — es gibt nichts, worauf die Differenz verteilt werden koennte. Zuerst eine weitere Stufe anlegen oder eine bezahlte Stufe erstatten.`,
    };
  }

  const zuVerteilenCents = angebotssummeCents - gebundeneSummeCents;
  // Jede offene Stufe braucht mindestens einen Cent: 0,00 EUR ist kein Betrag fuer eine offene
  // Forderung, und eine Stufe stillschweigend auf null zu setzen (oder gar zu stornieren) waere
  // eine Handlung, die der Betreiber nicht angefordert hat. Der Fall deckt zugleich den
  // wichtigeren ab: die neue Summe liegt unter dem bereits Bezahlten.
  if (zuVerteilenCents < offeneIndizes.length) {
    return {
      ok: false,
      fehler: "nichts_zu_verteilen",
      meldung: `Fuer die ${offeneIndizes.length} offene${offeneIndizes.length === 1 ? " Stufe" : "n Stufen"} bleibt nichts uebrig: bereits gebunden sind ${euro(gebundeneSummeCents)}, die neue Angebotssumme betraegt ${euro(angebotssummeCents)}. Hier hilft keine Umverteilung — die zu viel gezahlten Betraege muessen erstattet oder Stufen storniert werden.`,
    };
  }

  let offeneSummeAltCents = 0;
  for (const cents of offeneAltCents) offeneSummeAltCents += cents;

  // Ohne Gewichte (alle offenen Stufen auf 0,00 EUR — kaputte Daten, aber es gibt sie) ist jede
  // anteilige Rechnung eine Division durch null. Gleichmaessig ist dann die einzige Verteilung,
  // die nichts erfindet.
  const gewichte = offeneSummeAltCents > 0 ? offeneAltCents : offeneAltCents.map(() => 1);
  let gewichtSumme = 0;
  for (const g of gewichte) gewichtSumme += g;

  // Verfahren des groessten Restes: abgerundeter Anteil, dann die uebrigen Cent nach Groesse des
  // abgeschnittenen Bruchteils, bei Gleichstand an die vordere Stufe (deterministisch).
  const anteile: number[] = [];
  const reste: { position: number; rest: number }[] = [];
  let verteilt = 0;
  for (let p = 0; p < gewichte.length; p += 1) {
    const exakt = (zuVerteilenCents * (gewichte[p] as number)) / gewichtSumme;
    const abgerundet = Math.floor(exakt);
    anteile.push(abgerundet);
    reste.push({ position: p, rest: exakt - abgerundet });
    verteilt += abgerundet;
  }
  let uebrig = zuVerteilenCents - verteilt;
  reste.sort((a, b) => (b.rest === a.rest ? a.position - b.position : b.rest - a.rest));
  for (let k = 0; k < reste.length && uebrig > 0; k += 1) {
    const position = (reste[k] as { position: number }).position;
    anteile[position] = (anteile[position] as number) + 1;
    uebrig -= 1;
  }

  // Ein Cent Mindestanteil, ohne die Summe zu verlassen: was eine Nullstufe bekommt, gibt die
  // groesste Stufe ab. Dass genug da ist, garantiert die Pruefung `zuVerteilenCents >= Anzahl`.
  for (let p = 0; p < anteile.length; p += 1) {
    if ((anteile[p] as number) > 0) continue;
    let groesste = 0;
    for (let q = 1; q < anteile.length; q += 1) if ((anteile[q] as number) > (anteile[groesste] as number)) groesste = q;
    anteile[groesste] = (anteile[groesste] as number) - 1;
    anteile[p] = 1;
  }

  const aenderungen: Neuverteilungsaenderung[] = offeneIndizes.map((index, p) => ({
    index,
    altCents: offeneAltCents[p] as number,
    neuCents: anteile[p] as number,
  }));

  return { ok: true, aenderungen, gebundeneSummeCents, offeneSummeAltCents, offeneSummeNeuCents: zuVerteilenCents };
}
